/**
 * Mode-aware data hooks (`empty` / `mock` / `api`).
 *
 * Rewritten during the XON-102 audit for two reasons.
 *
 * **1. It was the main reason demo data shipped to production.** The file had
 * eight static top-level imports pulling in `emptyData`, `mockMyLearningData`,
 * `mockGraphData`, `mockMindspaceData` and `mockLearningData`, and the old
 * `useData` helper took `mockData` as an already-evaluated value — so the
 * fixtures were not just bundled but *constructed* on every hook call in every
 * mode. `pages/HomePage.tsx` reaches this module in three hops from the eagerly
 * imported `/` route, so those ~5k lines were in the entry chunk.
 *
 * Datasets now arrive through the async loaders in `@/data`, called inside the
 * React Query `queryFn` (already async, so no extra render pass). Rollup emits
 * them as separate chunks that a production build never requests.
 *
 * **2. Half of it was dead.** Of eight exported hooks, four had no consumer —
 * `useMyLearningData`, `useKnowledgeArchiveData`, `usePracticeArenaData`,
 * `useQuickBytesData` — and they were the only thing keeping
 * `mockMindspaceData.ts` and `types/mindspace.ts`'s mock-shaped type universe
 * in the import graph. Removed. The four that remain have real callers:
 * `useSearchHistory` / `useSuggestedTopics` / `useFirstTimeUser`
 * (`LearningInterface.tsx`) and `useMindspaceData` (`useMindspace.ts`).
 *
 * The bulk re-export block at the bottom of the old file is also gone: it
 * re-exported fourteen fixture values, which meant importing *any* hook from
 * here dragged every dataset in.
 */
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import type { DataProviderResult } from '@/config/data';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  loadEmptyGraphData,
  loadEmptyHomeData,
  loadMockGraphData,
  loadMockHomeData,
} from '@/data';
import { mindspaceApi } from '@/lib/api-client';
import type {
  GraphData,
  LearningTopic,
  SearchHistory,
} from '@/types/learning-data';
import { validateGraphData } from '@/utils/graphValidation';

export const dataProviderKeys = {
  all: ['dataProvider'] as const,
  mindspace: () => [...dataProviderKeys.all, 'mindspace'] as const,
  searchHistory: (userId: string, limit: number) =>
    [...dataProviderKeys.all, 'searchHistory', userId, limit] as const,
  suggestedTopics: (count: number) =>
    [...dataProviderKeys.all, 'suggestedTopics', count] as const,
  firstTimeUser: (userId: string) =>
    [...dataProviderKeys.all, 'firstTimeUser', userId] as const,
};

const defaultQueryOptions = {
  staleTime: 5 * 60 * 1000, // Data fresh for 5 minutes
  gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  retry: 2,
  refetchOnWindowFocus: false,
};

interface UseDataConfig<T> {
  /** Loads the `empty`-mode dataset. Async so the module can be code-split. */
  loadEmpty: () => Promise<T>;
  /** Loads the `mock`-mode dataset. Async for the same reason. */
  loadMock: () => Promise<T>;
  /** Fetches real data in `api` mode. Omit when there is no endpoint yet. */
  apiFetcher?: () => Promise<T>;
  supportsApi: boolean;
  queryKey: readonly unknown[];
  queryOptions?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>;
}

/**
 * Generic mode-aware fetch.
 *
 * `data` is `undefined` until the query resolves — the old version fell back to
 * an eagerly-built `emptyData` value, which is exactly what forced the fixtures
 * to be evaluated up front. Callers already handle `isLoading`.
 */
function useData<T>(config: UseDataConfig<T>): DataProviderResult<T> {
  const { mode, isMock, isApi, isEmpty } = useDataMode();
  const {
    loadEmpty,
    loadMock,
    apiFetcher,
    supportsApi,
    queryKey,
    queryOptions,
  } = config;

  const query = useQuery<T, Error>({
    queryKey: [...queryKey, mode],
    queryFn: async () => {
      if (isEmpty) {
        return await loadEmpty();
      }

      if (isMock) {
        await new Promise(resolve => setTimeout(resolve, 100));
        return await loadMock();
      }

      if (isApi) {
        if (supportsApi && apiFetcher) {
          return await apiFetcher();
        }
        // No endpoint yet — the component shows its "coming soon" state.
        return await loadEmpty();
      }

      return await loadMock();
    },
    ...defaultQueryOptions,
    ...queryOptions,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    mode,
    apiSupported: supportsApi,
  };
}

/**
 * Mindspace graph data. API Support: true (`/api/mindspace/graph`).
 *
 * The mock graph is validated inside the loader rather than at module scope.
 * The old code kept a module-level `validatedMockGraph` so react-d3-graph saw a
 * stable reference and did not reset its simulation (which reset zoom on every
 * parent re-render) — that stability now comes from React Query's cache, which
 * hands back the same object identity until the entry is invalidated.
 */
export function useMindspaceData(): DataProviderResult<GraphData> {
  return useData({
    loadEmpty: loadEmptyGraphData,
    loadMock: async () =>
      validateGraphData(await loadMockGraphData()) as GraphData,
    apiFetcher: async () => {
      const apiData = await mindspaceApi.getGraph();
      return validateGraphData({
        nodes: apiData.nodes || [],
        links: apiData.links || [],
      }) as GraphData;
    },
    supportsApi: true,
    queryKey: dataProviderKeys.mindspace(),
    queryOptions: {
      refetchInterval: 15 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  });
}

/** Search history. API Support: false (no backend endpoint yet). */
export function useSearchHistory(
  userId: string,
  limit: number = 2
): DataProviderResult<SearchHistory[]> {
  return useData({
    loadEmpty: async () => (await loadEmptyHomeData()).searchHistory,
    loadMock: async () =>
      (await loadMockHomeData()).getUserSearchHistory(userId).slice(0, limit),
    supportsApi: false,
    queryKey: dataProviderKeys.searchHistory(userId, limit),
  });
}

/** Suggested topics. API Support: false (no backend endpoint yet). */
export function useSuggestedTopics(
  count: number = 6
): DataProviderResult<LearningTopic[]> {
  return useData({
    loadEmpty: async () => (await loadEmptyHomeData()).suggestedTopics,
    loadMock: async () =>
      (await loadMockHomeData()).getRandomSuggestedTopics(count),
    supportsApi: false,
    queryKey: dataProviderKeys.suggestedTopics(count),
  });
}

/** First-time-user check. API Support: false (no backend endpoint yet). */
export function useFirstTimeUser(userId: string): {
  isFirstTime: boolean;
  isLoading: boolean;
  mode: string;
  apiSupported: boolean;
} {
  const { mode, isEmpty } = useDataMode();

  const query = useQuery({
    queryKey: [...dataProviderKeys.firstTimeUser(userId), mode],
    queryFn: async () => {
      if (isEmpty) {
        return (await loadEmptyHomeData()).isFirstTimeUser(userId);
      }
      // `api` mode has no endpoint yet and deliberately shares the mock path,
      // so the home page stays populated rather than claiming every user is
      // brand new.
      return (await loadMockHomeData()).isFirstTimeUser(userId);
    },
    ...defaultQueryOptions,
  });

  return {
    isFirstTime: query.data ?? false,
    isLoading: query.isLoading,
    mode,
    apiSupported: false,
  };
}
